/**
 * KeggActivityPanel.jsx — iter884 (Feb 2026)
 *
 * "🧬 KEGG Активност" — интегриран heat-map на ВСИЧКИ активни
 * метаболитно / възпалителни сигнални пътища, извлечени от приетите
 * състояния (епикризи, лаборатории, ръчни записи). Дава на жената и
 * лекаря "biology at a glance" — без да ровят из отделни секции.
 *
 * Design constraints (user request):
 *   • Показва активни pathways сортирани по натовареност.
 *   • Heat-map чрез цвят + брой (критично > високо > умерено > ниско).
 *   • БЕЗ per-pathway evidence links. Contributors се виждат само в
 *     tooltip / expand-on-click.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2, ExternalLink, Activity, Info,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { useHealthEvent } from "@/utils/healthEvents";

const TX = {
  bg: {
    title: "🧬 KEGG Активност",
    subtitle:
      "Активни метаболитно-възпалителни сигнални пътища от приетите Ви състояния (епикризи, лаборатории). Кликнете за детайли.",
    loading: "Пресмятаме активността…",
    empty: "Все още няма активни pathways. Приемете диагнози от епикриза или лабораторно изследване, за да се появят тук.",
    activeLabel: "активни",
    totalConditions: "от приети състояния",
    load: "натовареност",
    contributors: "Активирано от",
    intensity: {
      low:      "ниско",
      medium:   "умерено",
      high:     "високо",
      critical: "критично",
    },
  },
  en: {
    title: "🧬 KEGG Activity",
    subtitle:
      "Active metabolic-inflammatory signalling pathways from your accepted conditions (epicrises, labs). Click for details.",
    loading: "Computing activity…",
    empty: "No active pathways yet. Accept diagnoses from an epicrisis or lab report to see them here.",
    activeLabel: "active",
    totalConditions: "from accepted conditions",
    load: "load",
    contributors: "Activated by",
    intensity: { low: "low", medium: "medium", high: "high", critical: "critical" },
  },
};

const INTENSITY_STYLE = {
  low:      { chip: "bg-blue-500/15   text-blue-600   dark:text-blue-300",   dot: "bg-blue-400" },
  medium:   { chip: "bg-amber-500/20  text-amber-700  dark:text-amber-300",  dot: "bg-amber-400" },
  high:     { chip: "bg-orange-500/25 text-orange-700 dark:text-orange-300", dot: "bg-orange-500" },
  critical: { chip: "bg-red-500/25    text-red-700    dark:text-red-300",    dot: "bg-red-500" },
};

export default function KeggActivityPanel({ lang = "bg" }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/documents/kegg-activity`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [API]);
  useEffect(() => { load(); }, [load]);

  useHealthEvent("conditions-changed", () => load());

  const cardCls = `rounded-2xl border ${
    isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"
  }`;

  if (loading) {
    return (
      <div className={`${cardCls} p-5 mb-4`} data-testid="kegg-activity-loading">
        <div className="flex items-center gap-2 text-sm opacity-70">
          <Loader2 className="w-4 h-4 animate-spin" /> {t.loading}
        </div>
      </div>
    );
  }

  const pathways = Array.isArray(data?.pathways) ? data.pathways : [];

  return (
    <div className={`${cardCls} p-5 mb-4`} data-testid="kegg-activity-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shrink-0">
          <Activity className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-lg font-extrabold">{t.title}</h3>
            {pathways.length > 0 && (
              <div className="text-[11px] font-bold opacity-80">
                <span className={isDark ? "text-purple-300" : "text-purple-600"}>{data?.total_active ?? 0}</span>{" "}
                <span className="opacity-70">{t.activeLabel}</span>
                <span className="opacity-40"> · </span>
                <span className={isDark ? "text-pink-300" : "text-pink-600"}>{data?.total_conditions ?? 0}</span>{" "}
                <span className="opacity-70">{t.totalConditions}</span>
              </div>
            )}
          </div>
          <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>{t.subtitle}</p>
        </div>
      </div>

      {pathways.length === 0 ? (
        <div className="py-8 text-center text-sm opacity-70" data-testid="kegg-activity-empty">{t.empty}</div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="kegg-activity-list">
          {pathways.map((p) => {
            const style = INTENSITY_STYLE[p.intensity] || INTENSITY_STYLE.low;
            const isOpen = expanded === p.id;
            const barWidth = Math.min(100, (p.load / Math.max(pathways[0]?.load || 1, 1)) * 100);
            return (
              <li
                key={p.id}
                className={`p-2.5 rounded-xl border ${
                  isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"
                }`}
                data-testid={`kegg-activity-item-${p.id}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`w-2 h-2 rounded-full ${style.dot} shrink-0`} />
                  <a
                    href={p.kegg ? `https://www.kegg.jp/pathway/${p.kegg}` : undefined}
                    target={p.kegg ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="text-[11px] font-bold truncate flex-1 hover:underline flex items-center gap-1 min-w-0"
                    title={p.name_bg}
                  >
                    <span className="truncate">{p.name_bg}</span>
                    {p.kegg && <ExternalLink className="w-2.5 h-2.5 opacity-50 shrink-0" />}
                  </a>
                  <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded-full ${style.chip} shrink-0`}>
                    ×{p.load}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className={`h-1 flex-1 rounded-full overflow-hidden ${isDark ? "bg-white/10" : "bg-gray-200"}`}>
                    <div
                      className={`h-full ${style.dot} transition-all duration-500`}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                  {p.contributors && p.contributors.length > 0 && (
                    <button
                      onClick={() => setExpanded(isOpen ? null : p.id)}
                      className={`p-0.5 rounded ${isDark ? "hover:bg-white/10" : "hover:bg-gray-200"} shrink-0`}
                      data-testid={`kegg-activity-toggle-${p.id}`}
                      title={t.contributors}
                    >
                      <Info className="w-2.5 h-2.5 opacity-60" />
                    </button>
                  )}
                </div>
                {isOpen && p.contributors && p.contributors.length > 0 && (
                  <div className="mt-1.5 text-[10px] opacity-80 leading-snug">
                    <b>{t.contributors}:</b> {p.contributors.join(", ")}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
